import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { nanoid } from 'nanoid';
import {
  createTenantIndexedDBStorage,
  reviveReplayStoreDates,
  type PersistedReplayStoreState,
} from './tenantStorage';
import type {
  ReplayStore,
  ReplayCollection,
  ReplaySession,
  ReplayRequest,
  ReplayResponse,
  ReplaySendOptions,
} from '../types/replay';
import type { HttpRequest } from './proxy-store';
import { useProxyStore } from './proxy-store';
import { useProjectStore } from './projectStore';
import { buildUpstreamProxyFromSettings } from '../electron-shared/utils/proxy.util';

const createDefaultCollection = (): ReplayCollection => ({
  id: nanoid(),
  name: 'Default Collection',
  description: 'Default collection for replay requests',
  createdAt: new Date(),
  updatedAt: new Date(),
});

export const useReplayStore = create<ReplayStore>()(
  persist(
    (set, get) => ({
      // Initial state
      collections: [createDefaultCollection()],
      sessions: [],
      activeSessionId: null,
      isLoading: false,

      // Collection actions
      createCollection: (name: string, description?: string) => {
        const newCollection: ReplayCollection = {
          id: nanoid(),
          name,
          description,
          createdAt: new Date(),
          updatedAt: new Date(),
        };

        set((state) => ({
          collections: [...state.collections, newCollection],
        }));

        return newCollection.id;
      },

      updateCollection: (id: string, updates) => {
        set((state) => ({
          collections: state.collections.map((collection) =>
            collection.id === id
              ? { ...collection, ...updates, updatedAt: new Date() }
              : collection,
          ),
        }));
      },

      deleteCollection: (id: string) => {
        set((state) => {
          // Don't delete if it's the last collection
          if (state.collections.length <= 1) return state;

          // Move sessions to the first remaining collection
          const remainingCollections = state.collections.filter(
            (c) => c.id !== id,
          );
          const defaultCollectionId = remainingCollections[0].id;

          return {
            collections: remainingCollections,
            sessions: state.sessions.map((session) =>
              session.collectionId === id
                ? { ...session, collectionId: defaultCollectionId }
                : session,
            ),
          };
        });
      },

      // Session actions
      createSession: (
        collectionId: string,
        name: string,
        initialRequest?: ReplayRequest,
      ) => {
        const newSession: ReplaySession = {
          id: nanoid(),
          name,
          collectionId,
          requests: initialRequest ? [initialRequest] : [],
          responses: [],
          currentRequestIndex: 0,
          createdAt: new Date(),
          updatedAt: new Date(),
        };

        set((state) => ({
          sessions: [...state.sessions, newSession],
          activeSessionId: newSession.id,
        }));

        return newSession.id;
      },

      updateSession: (id: string, updates) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === id
              ? { ...session, ...updates, updatedAt: new Date() }
              : session,
          ),
        }));
      },

      deleteSession: (id: string) => {
        set((state) => ({
          sessions: state.sessions.filter((session) => session.id !== id),
          activeSessionId:
            state.activeSessionId === id ? null : state.activeSessionId,
        }));
      },

      duplicateSession: (id: string, newName?: string) => {
        const session = get().sessions.find((s) => s.id === id);

        if (!session) return '';

        const duplicatedSession: ReplaySession = {
          ...session,
          id: nanoid(),
          name: newName || `${session.name} (Copy)`,
          createdAt: new Date(),
          updatedAt: new Date(),
        };

        set((state) => ({
          sessions: [...state.sessions, duplicatedSession],
        }));

        return duplicatedSession.id;
      },

      moveSession: (sessionId: string, targetCollectionId: string) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === sessionId
              ? {
                  ...session,
                  collectionId: targetCollectionId,
                  updatedAt: new Date(),
                }
              : session,
          ),
        }));
      },

      // Request actions
      addRequestToSession: (sessionId: string, request: ReplayRequest) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === sessionId
              ? {
                  ...session,
                  requests: [...session.requests, request],
                  currentRequestIndex: session.requests.length, // Point to new request
                  updatedAt: new Date(),
                }
              : session,
          ),
        }));
      },

      updateRequestInSession: (
        sessionId: string,
        requestId: string,
        updates,
      ) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === sessionId
              ? {
                  ...session,
                  requests: session.requests.map((request) =>
                    request.id === requestId
                      ? { ...request, ...updates }
                      : request,
                  ),
                  updatedAt: new Date(),
                }
              : session,
          ),
        }));
      },

      deleteRequestFromSession: (sessionId: string, requestId: string) => {
        set((state) => ({
          sessions: state.sessions.map((session) => {
            if (session.id !== sessionId) return session;

            const updatedRequests = session.requests.filter(
              (request) => request.id !== requestId,
            );
            const newCurrentIndex = Math.min(
              session.currentRequestIndex,
              Math.max(0, updatedRequests.length - 1),
            );

            return {
              ...session,
              requests: updatedRequests,
              currentRequestIndex: newCurrentIndex,
              updatedAt: new Date(),
            };
          }),
        }));
      },

      // Response actions
      addResponseToSession: (sessionId: string, response: ReplayResponse) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === sessionId
              ? {
                  ...session,
                  responses: [...session.responses, response],
                  updatedAt: new Date(),
                }
              : session,
          ),
        }));
      },

      // Navigation
      setActiveSession: (sessionId: string | null) => {
        set({ activeSessionId: sessionId });
      },

      navigateSessionHistory: (
        sessionId: string,
        direction: 'prev' | 'next',
      ) => {
        set((state) => ({
          sessions: state.sessions.map((session) => {
            if (session.id !== sessionId) return session;

            const maxIndex = session.requests.length - 1;
            let newIndex = session.currentRequestIndex;

            if (direction === 'prev' && newIndex > 0) {
              newIndex--;
            } else if (direction === 'next' && newIndex < maxIndex) {
              newIndex++;
            }

            return {
              ...session,
              currentRequestIndex: newIndex,
              updatedAt: new Date(),
            };
          }),
        }));
      },

      // Utilities
      createRequestFromHttpRequest: (
        httpRequest: HttpRequest,
      ): ReplayRequest => {
        return {
          id: nanoid(),
          name: `${httpRequest.method} ${new URL(httpRequest.url).pathname}`,
          method: httpRequest.method,
          url: httpRequest.url,
          headers: httpRequest.headers || {},
          body: httpRequest.body || '',
          rawHeaders: httpRequest.rawHeaders,
          timestamp: new Date(),
          originalRequestId: httpRequest.id,
        };
      },

      sendReplayRequest: async (
        request: ReplayRequest,
        options?: ReplaySendOptions,
      ): Promise<ReplayResponse> => {
        const startTime = Date.now();

        try {
          // Send request through Electron API
          if (typeof window !== 'undefined' && window.electronAPI) {
            const { targetId, targetType } = useProjectStore.getState();
            const proxySettings = useProxyStore.getState().settings;
            const upstreamProxy = buildUpstreamProxyFromSettings({
              upstreamEnabled: !!proxySettings.upstreamEnabled,
              upstreamHost: proxySettings.upstreamHost,
              upstreamPort: proxySettings.upstreamPort,
              upstreamAuth: proxySettings.upstreamAuth,
            });
            const result = await window.electronAPI.replay.sendRequest({
              method: request.method,
              url: request.url,
              headers: request.headers,
              body: request.body,
              rawHeaders: request.rawHeaders,
              targetId: targetId || 'standalone',
              targetType,
              bodyEncoding: request.bodyEncoding,
              protocol: options?.protocol,
              preserveContentLength: options?.preserveContentLength,
              connectionTarget: options?.connectionTarget,
              replayMtls: options?.replayMtls,
              source: options?.source,
              threadId: options?.threadId,
              upstreamProxy,
            });

            if (result.success && result.data) {
              return {
                id: nanoid(),
                requestId: request.id,
                statusCode: result.data.statusCode,
                statusMessage: result.data.statusMessage || '',
                headers: result.data.headers || {},
                rawHeaders: result.data.rawHeaders,
                body: result.data.body || '',
                timestamp: new Date(),
                duration: Date.now() - startTime,
                httpVersion: result.data.httpVersion,
                historyRequestId: result.data.historyRequestId,
              };
            } else {
              throw new Error(result.error || 'Request failed');
            }
          } else {
            throw new Error('Electron API not available');
          }
        } catch (error) {
          return {
            id: nanoid(),
            requestId: request.id,
            statusCode: 0,
            statusMessage: '',
            headers: {},
            body: '',
            timestamp: new Date(),
            duration: Date.now() - startTime,
            error: error instanceof Error ? error.message : 'Unknown error',
          };
        }
      },

      clearAllData: () => {
        set({
          collections: [createDefaultCollection()],
          sessions: [],
          activeSessionId: null,
          isLoading: false,
        });
      },
    }),
    {
      name: 'terra-proxy-replay-store',
      storage: createJSONStorage(() => createTenantIndexedDBStorage()),
      partialize: (state) => ({
        collections: state.collections,
        sessions: state.sessions.map((session) => ({
          ...session,
          requests: session.requests.slice(-10),
          responses: session.responses.slice(-10),
        })),
      }),
      merge: (persistedState, currentState) => ({
        ...currentState,
        ...reviveReplayStoreDates(persistedState as PersistedReplayStoreState),
      }),
    },
  ),
);
